import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /**
   * When any value in this array changes, a caught error is cleared and the
   * children are re-rendered. Route-level boundaries pass the current pathname
   * so that navigating away from a broken screen recovers automatically.
   */
  resetKeys?: ReadonlyArray<unknown>;
  /** Heading shown in the fallback. Defaults to a generic app-level message. */
  title?: string;
  /** Sentence shown under the heading, describing what the user can still do. */
  description?: string;
  /** Hides the "Back to board" link — used by the outermost boundary, where routing may itself be broken. */
  hideBoardLink?: boolean;
}

interface ErrorBoundaryState {
  error: Error | null;
}

function keysChanged(a: ReadonlyArray<unknown>, b: ReadonlyArray<unknown>): boolean {
  return a.length !== b.length || a.some((value, i) => !Object.is(value, b[i]));
}

/**
 * Catches render/lifecycle exceptions from its subtree and shows a recoverable
 * fallback instead of letting React unmount the whole tree to a blank page.
 *
 * Why this exists: the app previously had no boundary anywhere, so a single
 * render throw — a malformed API row, an undefined field on a legacy DynamoDB
 * item — white-screened the entire console with no way back. On a clinical
 * board that is the worst possible failure mode, because it looks identical to
 * "the system is down" and destroys the user's trust in what they were reading.
 *
 * Two things it deliberately does NOT do:
 *  - It does not catch errors thrown in event handlers or async callbacks;
 *    React boundaries never see those. Those paths still need their own
 *    try/catch and user-visible error state.
 *  - It does not report anywhere. There is no error-reporting service wired up
 *    in this app yet; when one is added, `componentDidCatch` is the hook.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Keep the component stack — it is the only breadcrumb pointing at which
    // screen threw, and it is otherwise swallowed by the boundary.
    console.error('Unhandled render error:', error, errorInfo.componentStack);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    const prevKeys = prevProps.resetKeys ?? [];
    const nextKeys = this.props.resetKeys ?? [];
    if (this.state.error !== null && keysChanged(prevKeys, nextKeys)) {
      this.setState({ error: null });
    }
  }

  handleRetry = (): void => {
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const {
      title = 'Something went wrong',
      description = 'This screen ran into an unexpected error. Nothing you were viewing has been saved or changed.',
      hideBoardLink = false,
    } = this.props;

    return (
      <div className="card" role="alert" style={{ maxWidth: '640px', margin: '48px auto' }}>
        <h2 style={{ marginTop: 0, fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
          {title}
        </h2>
        <p style={{ color: 'var(--text-secondary)' }}>{description}</p>

        <details style={{ marginTop: '16px' }}>
          <summary style={{ cursor: 'pointer', color: 'var(--text-muted)' }}>Technical details</summary>
          <pre
            style={{
              marginTop: '8px',
              padding: '12px',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              background: 'var(--bg-input)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--danger-text)',
            }}
          >
            {error.message || String(error)}
          </pre>
        </details>

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-primary" onClick={this.handleRetry}>
            Try again
          </button>
          {!hideBoardLink && (
            <a className="btn btn-secondary" href="/board">
              Back to board
            </a>
          )}
          <button type="button" className="btn btn-secondary" onClick={() => window.location.reload()}>
            Reload the app
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
